/**
 * <ringg-widget> — top-level custom element that orchestrates the entire widget.
 *
 * Usage:
 *   <ringg-widget
 *     agent-id="xxx"
 *     x-api-key="yyy"
 *     mode="stage"
 *   ></ringg-widget>
 *
 * Or via JS:
 *   const el = document.createElement("ringg-widget");
 *   el.config = { agentId: "xxx", xApiKey: "yyy", variables: {} };
 *   document.body.appendChild(el);
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import {
  RinggApiClient,
  StaticUrlResolver,
  DomEventBus,
  WidgetStateController,
  mergeWidgetTheme,
  colorToSolid,
  type RinggWidgetConfig,
  type EnvironmentUrls,
  type EnvironmentMode,
  type MediaType,
  type ChatMessage,
  type ResolvedWidgetTheme,
  type SimpleComponentPayload,
  type InteractiveFlowPayload,
  type SlashCommand,
} from "@ringg/core";

import { LiveKitWebAdapter } from "../adapters/livekit-web-adapter";
import { ConnectionController } from "../controllers/connection.controller";
import { ChatController } from "../controllers/chat.controller";
import { TranscriptController } from "../controllers/transcript.controller";
import { ComponentController } from "../controllers/component.controller";
import { FeedbackController } from "../controllers/feedback.controller";
import { hostTokens, sharedStyles } from "../styles/tokens";
import { icon } from "../utils/icons";
import { renderMarkdown } from "../utils/markdown";

// Import sub-components so they register themselves
import "./ui/widget-trigger";
import "./ui/widget-header";
import "./ui/tab-selector";
import "./ui/action-button";
import "./ui/call-controls";
import "./ui/message-bubble";
import "./ui/feedback-screen";
import "./ui/slash-command-menu";
import "./steps/interactive-flow";
import "./steps/calendar-step";
import "./steps/form-step";
import "./steps/buttons-step";
import "./steps/confirmation-step";
import "./steps/policy-finder/policy-finder-widget";

// ─── Default URLs ────────────────────────────────────────────────────────────

const DEFAULT_URLS: Record<EnvironmentMode, EnvironmentUrls> = {
  prod: {
    backendUrl: "https://prod-api.ringg.ai/ca/api/v0",
    roomServerUrl: "wss://ringg-ai-prod-r0vhjzhs.livekit.cloud",
  },
  stage: {
    backendUrl: "https://stage-api2.desivocal.com/ca/api/v0",
    roomServerUrl: "wss://ringg-ai-ujrjmhgy.livekit.cloud",
  },
  dev: {
    backendUrl: "https://calling-dev.ringg.ai/ca/api/v0",
    roomServerUrl: "wss://ringg-ai-dev-92tubwpz.livekit.cloud",
  },
};

@customElement("ringg-widget")
export class RinggWidget extends LitElement {
  static override styles = [
    hostTokens,
    sharedStyles,
    css`
      :host {
        display: contents;
      }

      .widget-panel {
        position: fixed;
        bottom: 2%;
        right: 2%;
        width: var(--ringg-widget-width);
        max-width: 96dvw;
        max-height: var(--ringg-widget-max-height);
        border: 1px solid var(--ringg-border);
        border-radius: var(--ringg-radius);
        background: var(--ringg-bg);
        box-shadow: var(--ringg-shadow-xl);
        font-family: var(--ringg-font-family);
        color: var(--ringg-text);
        overflow: hidden;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        animation: ringg-slide-up 0.3s ease;
      }

      .widget-panel.calling {
        height: 24.5rem;
      }

      /* Mobile: 95vw width */
      @media (max-width: 768px) {
        .widget-panel {
          width: 95vw;
        }
      }

      @keyframes ringg-slide-up {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .panel-content {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .panel-body {
        position: relative;
        padding: 1.25rem;
        height: 100%;
        margin-bottom: 0.5rem;
        display: flex;
        gap: 1.25rem;
        flex-direction: column;
      }

      .panel-body.precall {
        margin-top: 0.5rem;
      }

      .messages-area {
        margin-top: 5rem;
        height: 100%;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      }

      .messages-list {
        margin-top: auto;
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
      }

      .empty-area {
        margin-top: 5rem;
        height: 100%;
      }

      .error-text {
        text-align: center;
        font-size: var(--ringg-font-size-sm);
        color: var(--ringg-error);
        margin-top: -0.75rem;
        margin-bottom: -0.5rem;
      }

      .legal-text {
        font-size: var(--ringg-font-size-xs);
        text-align: center;
        color: var(--ringg-muted);
      }

      .legal-text a {
        color: var(--ringg-primary);
      }

      .legal-text a:hover {
        text-decoration: underline;
      }

      .close-btn,
      .minimize-btn {
        position: absolute;
        z-index: 9999;
        top: 0.625rem;
        width: 36px;
        height: 36px;
        border-radius: var(--ringg-radius-full);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
        transition: color var(--ringg-transition);
      }

      .close-btn:hover,
      .minimize-btn:hover {
        color: #374151;
      }

      .close-btn {
        right: 0.625rem;
      }

      .minimize-btn {
        right: 3rem;
      }

      .close-btn svg,
      .minimize-btn svg {
        pointer-events: none;
        flex-shrink: 0;
      }

      /* End-call confirmation dialog */
      .dialog-overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 20;
        animation: ringg-fade-in 0.15s ease;
      }

      @keyframes ringg-fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .dialog-box {
        background: var(--ringg-bg);
        border-radius: var(--ringg-radius);
        padding: 1.5rem;
        margin: 1rem;
        max-width: 300px;
        width: 100%;
        box-shadow: var(--ringg-shadow);
      }

      .dialog-title {
        font-size: var(--ringg-font-size-base);
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      .dialog-desc {
        font-size: var(--ringg-font-size-sm);
        color: var(--ringg-muted);
        margin-bottom: 1rem;
      }

      .dialog-actions {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
      }

      .dialog-actions button {
        padding: 0.5rem 1rem;
        border-radius: var(--ringg-button-radius);
        font-size: var(--ringg-font-size-sm);
        font-weight: 500;
        transition: opacity var(--ringg-transition);
      }

      .dialog-actions button:hover {
        opacity: 0.85;
      }

      .dialog-cancel {
        border: 1px solid var(--ringg-border);
        background: var(--ringg-surface);
        color: var(--ringg-text);
      }

      .dialog-confirm {
        background: var(--ringg-primary);
        color: var(--ringg-primary-text);
      }
    `,
  ];

  // ─── Public properties (set via attributes or JS) ─────────────────────────

  /** Full config object — alternative to individual attributes. */
  @property({ type: Object })
  config: RinggWidgetConfig = { agentId: "", xApiKey: "", variables: {} };

  /** Agent ID (attribute shorthand) */
  @property({ attribute: "agent-id" })
  agentId = "";

  /** API key (attribute shorthand) */
  @property({ attribute: "x-api-key" })
  xApiKey = "";

  /** Environment mode */
  @property()
  mode: EnvironmentMode = "prod";

  // ─── Internal state ──────────────────────────────────────────────────────

  @state() private _isOpen = false;
  @state() private _showEndCallDialog = false;
  @state() private _initialized = false;
  private _lastMessageCount = 0;

  // ─── Core instances ──────────────────────────────────────────────────────

  private _adapter!: LiveKitWebAdapter;
  private _api!: RinggApiClient;
  private _eventBus!: DomEventBus;
  private _widgetState!: WidgetStateController;
  private _theme!: ResolvedWidgetTheme;
  private _notificationAudio: HTMLAudioElement | null = null;

  // ─── Controllers ─────────────────────────────────────────────────────────

  private _connection!: ConnectionController;
  private _chat!: ChatController;
  private _transcripts!: TranscriptController;
  private _components!: ComponentController;
  private _feedback!: FeedbackController;

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();
    this._initialize();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._adapter?.dispose();
    this._widgetState?.dispose();
    this._eventBus?.off();
    if (this._notificationAudio) {
      this._notificationAudio.pause();
      this._notificationAudio.src = "";
      this._notificationAudio = null;
    }
  }

  private _initialize(): void {
    if (this._initialized) return;

    // Resolve config from attributes or config object
    const resolvedConfig = this._resolveConfig();

    const configMode = resolvedConfig.mode ?? "prod";
    const urls = DEFAULT_URLS;

    this._adapter = new LiveKitWebAdapter();
    this._eventBus = new DomEventBus();

    this._api = new RinggApiClient({
      urlResolver: new StaticUrlResolver(urls),
      mode: configMode,
      xApiKey: resolvedConfig.xApiKey,
    });

    this._widgetState = new WidgetStateController(this._eventBus, resolvedConfig.defaultTab ?? "audio");

    this._theme = mergeWidgetTheme(resolvedConfig.theme);

    // Create controllers
    this._connection = new ConnectionController(this, {
      adapter: this._adapter,
      api: this._api,
      widgetState: this._widgetState,
      roomServerUrl: urls[configMode].roomServerUrl,
      agentId: resolvedConfig.agentId,
      variables: resolvedConfig.variables,
      defaultTab: resolvedConfig.defaultTab,
    });

    this._chat = new ChatController(this, this._adapter);
    this._transcripts = new TranscriptController(this, this._adapter);
    this._components = new ComponentController(this, this._adapter, this._eventBus);
    this._feedback = new FeedbackController(this, this._api, this._widgetState, this._eventBus);

    // Source URL wiring: ComponentController.lastSourceUrl -> ChatController.sourceUrl
    // This is done in updated() lifecycle to stay in sync on every render cycle.

    // Notification sound
    const tuneUrl = resolvedConfig.notificationTuneUrl ?? "https://assets.ringg.ai/audio/notification.mp3";
    this._notificationAudio = new Audio(tuneUrl);
    this._notificationAudio.preload = "auto";

    this._initialized = true;
  }

  private _resolveConfig(): RinggWidgetConfig {
    // If full config object is provided, use it
    if (this.config.agentId) return this.config;

    // Otherwise build from attributes, allowing config overrides for non-required fields
    return {
      ...this.config,
      agentId: this.agentId || this.config.agentId,
      xApiKey: this.xApiKey || this.config.xApiKey,
      variables: this.config.variables ?? {},
      mode: this.mode || this.config.mode,
    };
  }

  // ─── Event handlers ────────────────────────────────────────────────────

  private _toggle(): void {
    this._isOpen = !this._isOpen;
    if (this._isOpen) {
      this._widgetState.open();
    } else {
      this._widgetState.minimize();
    }
  }

  private _close(): void {
    if (this._connection.isConnected) {
      this._showEndCallDialog = true;
    } else if (this._widgetState.isShowingFeedback) {
      this._feedback.skip();
      this._isOpen = false;
    } else {
      this._isOpen = false;
    }
  }

  private _minimize(): void {
    this._isOpen = false;
  }

  private _confirmEndCall(): void {
    this._showEndCallDialog = false;
    this._chat.clearMessages();
    this._transcripts.clearTranscripts();
    this._components.clearComponents();
    this._connection.endCall();
  }

  private _cancelEndCall(): void {
    this._showEndCallDialog = false;
  }

  private async _handleStartCall(e: CustomEvent<{ mediaType: MediaType }>): Promise<void> {
    await this._connection.startCall(e.detail.mediaType);
  }

  private async _handleSendMessage(e: CustomEvent<{ text: string }>): Promise<void> {
    await this._chat.sendMessage(e.detail.text);
  }

  private _handleTabChange(e: CustomEvent<{ tab: MediaType }>): void {
    this._connection.callMode = e.detail.tab;
    this.requestUpdate();
  }

  private async _handleFeedbackSubmit(e: CustomEvent<{ rating: number; comment: string }>): Promise<void> {
    await this._feedback.submit(e.detail.rating, e.detail.comment);
    this._isOpen = false;
  }

  private _handleFeedbackSkip(): void {
    this._feedback.skip();
    this._isOpen = false;
  }

  private async _handleSlashCommand(e: CustomEvent<{ command: SlashCommand }>): Promise<void> {
    const cmd = e.detail.command;

    if (cmd.tool_type === "WIDGET_TOOL" || cmd.tool_type === "DISEASE_ACCEPTANCE_TOOL") {
      // Open disease widget immediately without sending RPC
      const componentId = `disease_data_${Date.now()}`;
      const msg: ChatMessage = {
        name: "Agent",
        message: "",
        isSelf: false,
        timestamp: Date.now() + Math.random(),
        componentType: "disease_data",
        componentData: {
          component_type: "disease_data",
          component_id: componentId,
          label: cmd.command,
        } as unknown as SimpleComponentPayload,
      };
      this._chat.addComponentMessage(msg);
      return;
    }

    await this._components.sendSlashCommand(cmd.command);
  }

  override updated(_changedProperties: Map<string, unknown>): void {
    // Auto-scroll messages area to bottom when new messages arrive
    const messages = this._connection?.callMode === "audio" ? this._transcripts?.transcripts : this._chat?.messages;
    if (messages && messages.length > this._lastMessageCount) {
      const isNewAgentMessage = messages.length > 0 && !messages[messages.length - 1].isSelf;
      this._lastMessageCount = messages.length;
      this._scrollToBottom();

      // Play notification sound on new agent message
      if (isNewAgentMessage) {
        this._playNotificationSound();
        // Auto-open widget if minimized and agent sends a message
        if (!this._isOpen) {
          this._isOpen = true;
          this._widgetState.open();
        }
      }
    }

    // Keep source URL in sync: ComponentController -> ChatController
    if (this._components?.lastSourceUrl) {
      this._chat.sourceUrl = this._components.lastSourceUrl;
    }
  }

  private _playNotificationSound(): void {
    if (this._notificationAudio) {
      this._notificationAudio.currentTime = 0;
      this._notificationAudio.play().catch(() => {
        // Autoplay blocked by browser — user hasn't interacted yet. Safe to ignore.
      });
    }
  }

  private _scrollToBottom(): void {
    requestAnimationFrame(() => {
      const area = this.shadowRoot?.querySelector(".messages-area");
      if (area) {
        area.scrollTop = area.scrollHeight;
      }
    });
  }

  /**
   * Scroll lock — prevents the underlying page from scrolling when user
   * scrolls inside the widget messages area and reaches the boundary.
   */
  private _handleScrollLock(e: Event): void {
    const el = e.currentTarget as HTMLElement;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    if (e instanceof WheelEvent) {
      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
      }
    }
    // For touchmove, we allow the event but stop propagation to prevent parent scroll
    if (e.type === "touchmove") {
      e.stopPropagation();
    }
  }

  private _escapeHtml(str: string): string {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  override render() {
    if (!this._initialized) return nothing;

    const config = this._resolveConfig();
    const isCalling = this._connection.isConnected;
    const messages = this._connection.callMode === "audio" ? this._transcripts.transcripts : this._chat.messages;

    // Merge config slash commands with runtime ones from webcall response (deduplicated)
    const configCmds = config.enabledSlashCommands ?? [];
    const seen = new Set(configCmds.map((c) => c.command));
    const mergedSlashCommands = [...configCmds];
    for (const cmd of this._connection.runtimeSlashCommands) {
      if (!seen.has(cmd.command)) mergedSlashCommands.push(cmd);
    }

    return html`
      ${!this._isOpen || !(config.widgetPosition?.hideTriggerOnExpand ?? true)
        ? html` <ringg-trigger .theme=${this._theme} .triggerIcon=${config.buttons?.modalTrigger?.icon?.url} .triggerIconSize=${config.buttons?.modalTrigger?.icon?.size} @trigger-click=${this._toggle}></ringg-trigger> `
        : nothing}
      ${this._isOpen
        ? html`
            <div
              class="widget-panel ${isCalling ? "calling" : ""}"
              style="
                --ringg-primary: ${colorToSolid(this._theme.primaryColor)};
                --ringg-primary-text: ${this._theme.primaryTextColor};
                --ringg-bg: ${this._theme.backgroundColor};
                --ringg-surface: ${this._theme.surfaceColor};
                --ringg-text: ${this._theme.textColor};
                --ringg-muted: ${this._theme.mutedTextColor};
                --ringg-border: ${this._theme.borderColor};
                --ringg-radius: ${this._theme.borderRadius};
                --ringg-font-family: ${this._theme.fontFamily};
                --ringg-button-radius: ${this._theme.buttonStyle === "pill" ? "9999px" : this._theme.buttonStyle === "square" ? "6px" : "12px"};
              "
            >
              ${isCalling ? html` <button class="minimize-btn" aria-label="Minimize" @click=${this._minimize}>${unsafeHTML(icon("minus", 20))}</button> ` : nothing}

              <button class="close-btn" aria-label="Close" @click=${this._close}>${unsafeHTML(icon("x", 20))}</button>

              ${this._showEndCallDialog ? this._renderEndCallDialog() : nothing}
              ${this._widgetState.isShowingFeedback
                ? html`
                    <ringg-feedback-screen
                      .callMode=${this._connection.callMode}
                      .isSubmitting=${this._feedback.isSubmitting}
                      .feedbackConfig=${config.feedbackScreen}
                      @feedback-submit=${this._handleFeedbackSubmit}
                      @feedback-skip=${this._handleFeedbackSkip}
                    ></ringg-feedback-screen>
                  `
                : html`
                    <div class="panel-content">
                      <div class="panel-body ${isCalling ? "" : "precall"}">
                        <ringg-header
                          .title=${config.title ?? "Ringg AI Support"}
                          .description=${config.description ?? "Ringg AI offers 24/7 voice support to handle your business calls efficiently and professionally."}
                          .logoUrl=${config.logoUrl}
                          .isCalling=${isCalling}
                          .isLoading=${this._connection.isLoading}
                          .theme=${this._theme}
                        ></ringg-header>

                        ${isCalling && messages.length === 0 ? html`<div class="empty-area"></div>` : nothing}
                        ${isCalling && messages.length > 0
                          ? html`
                              <div class="messages-area" role="log" aria-live="polite" aria-label="Conversation messages" @wheel=${this._handleScrollLock} @touchmove=${this._handleScrollLock}>
                                <div class="messages-list">${messages.map((msg, idx) => this._renderMessage(msg, idx))}</div>
                              </div>
                            `
                          : nothing}

                        <ringg-call-controls
                          .callMode=${this._connection.callMode}
                          .isCalling=${isCalling}
                          .isMicEnabled=${this._connection.isMicEnabled}
                          .slashCommands=${mergedSlashCommands}
                          @toggle-mic=${() => this._connection.toggleMic()}
                          @end-call=${() => this._connection.endCall()}
                          @send-message=${this._handleSendMessage}
                          @slash-command=${this._handleSlashCommand}
                        ></ringg-call-controls>

                        ${this._connection.error ? html`<div class="error-text">${this._connection.error}</div>` : nothing}
                        ${this._connection.connectionState === "disconnected"
                          ? html` <ringg-action-button .callMode=${this._connection.callMode} .isLoading=${this._connection.isLoading} .theme=${this._theme} @start-call=${this._handleStartCall}></ringg-action-button> `
                          : nothing}
                        ${!isCalling ? this._renderLegalDisclaimer(config) : nothing}
                      </div>

                      ${!isCalling && !(config.hideTabSelector ?? false) ? html` <ringg-tab-selector .activeTab=${this._connection.callMode} .theme=${this._theme} @tab-change=${this._handleTabChange}></ringg-tab-selector> ` : nothing}
                    </div>
                  `}
            </div>
          `
        : nothing}
    `;
  }

  private _renderMessage(msg: ChatMessage, index: number) {
    // Disease widget
    if (msg.componentType === "disease_data" && msg.componentData) {
      return html`
        <ringg-policy-finder
          .label=${(msg.componentData as any).label}
          .onSearch=${(q: string) => this._components.searchDiseases(q)}
          .onSubmit=${(data: any) => this._components.submitDiseaseWidget(msg.componentData!.component_id, data.selections)}
        ></ringg-policy-finder>
      `;
    }

    // Interactive flow
    if (msg.componentType && msg.componentData) {
      return html`
        <ringg-interactive-flow
          .initialComponent=${msg.componentData}
          .onApiCall=${(method: string, endpoint: string, payload: Record<string, string>) => this._api.callComponentApi(method as "POST" | "GET", endpoint, payload)}
          .onSendResponse=${(componentId: string, responseData: Record<string, unknown>) => this._components.respondToComponent(componentId, responseData)}
        ></ringg-interactive-flow>
      `;
    }

    // Regular message
    return html` <ringg-message-bubble .message=${msg} .theme=${this._theme}></ringg-message-bubble> `;
  }

  private _renderEndCallDialog() {
    const modeLabel = this._connection.callMode === "text" ? "chat" : "call";
    return html`
      <div class="dialog-overlay">
        <div class="dialog-box">
          <div class="dialog-title">Are you sure you want to end ${modeLabel}?</div>
          <div class="dialog-desc">Your current conversation will be closed and cannot be accessed later.</div>
          <div class="dialog-actions">
            <button class="dialog-cancel" @click=${this._cancelEndCall}>Cancel</button>
            <button class="dialog-confirm" @click=${this._confirmEndCall}>End ${modeLabel === "chat" ? "Chat" : "Call"}</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderLegalDisclaimer(config: RinggWidgetConfig) {
    if (config.legalDisclaimer) {
      // Custom disclaimer with links
      // links is Record<string, string> where key = display text, value = URL
      let text = this._escapeHtml(config.legalDisclaimer.text);
      if (config.legalDisclaimer.links) {
        for (const [linkText, linkUrl] of Object.entries(config.legalDisclaimer.links)) {
          const escapedText = this._escapeHtml(linkText);
          const escapedUrl = this._escapeHtml(linkUrl);
          text = text.replace(escapedText, `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedText}</a>`);
        }
      }
      return html`<p class="legal-text">${unsafeHTML(text)}</p>`;
    }

    return html`
      <p class="legal-text">
        By starting call you agree to
        <a href="https://www.ringg.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy policy</a>
        &
        <a href="https://www.ringg.ai/terms" target="_blank" rel="noopener noreferrer">T&C</a>
      </p>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ringg-widget": RinggWidget;
  }
}
